import { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Truck, MapPin, Clock, CheckCircle, Play, Pause, AlertTriangle, Camera, Upload, Loader2, MessageSquare, Send, Download, Smartphone } from 'lucide-react';

export default function DriverPortal() {
  const [loadNumber, setLoadNumber] = useState('');
  const [loadData, setLoadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  // Document upload state
  const [driverDocs, setDriverDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('bol');

  // Signature capture state  
  const [showSignature, setShowSignature] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [deliveryDateConfirm, setDeliveryDateConfirm] = useState(new Date().toISOString().split('T')[0]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submittingSignature, setSubmittingSignature] = useState(false);
  const canvasRef = useRef(null);

  // Incident reporting state
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [incidentType, setIncidentType] = useState('');
  const [incidentSeverity, setIncidentSeverity] = useState('medium');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [incidentImages, setIncidentImages] = useState([]);
  const [submittingIncident, setSubmittingIncident] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Messaging state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversation, setConversation] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (loadData && loadData.load.is_tracking_active) {
      setIsTracking(true);
      startLocationTracking();
    }
  }, [loadData]);

  // Load and poll messages when a load is active
  useEffect(() => {
    if (!loadData) return;
    loadMessages();
    loadDocuments();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallBanner(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      toast.success('App installed!');
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  const loadMessages = () => {
    if (!loadNumber) return;
    api.get(`/driver/load/${loadNumber}/messages`)
      .then(res => {
        setConversation(res.data.conversation);
        setMessages(res.data.messages || []);
      })
      .catch(() => {});
  };

  const loadDocuments = () => {
    if (!loadNumber) return;
    api.get(`/driver/load/${loadNumber}/documents`)
      .then(res => setDriverDocs(res.data || []))
      .catch(() => {});
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    formData.append('notes', `Uploaded from driver phone`);
    
    try {
      const res = await api.post(`/driver/load/${loadNumber}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  // Signature pad functions
  const initCanvas = () => {
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = canvas.offsetWidth;
      canvas.height = 200;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }, 100);
  };

  const startDraw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submitSignatureAndDropoff = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter the receiver name');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('Signature pad not ready');
      return;
    }

    setSubmittingSignature(true);
    try {
      const signatureData = canvas.toDataURL('image/png');
      
      await api.post(`/driver/load/${loadNumber}/sign-pod`, {
        signer_name: signerName,
        delivery_date: deliveryDateConfirm,
        signature_data: signatureData
      });
      
      // Now update status to dropped_off
      await api.post(`/driver/load/${loadNumber}/update-status`, {
        status: 'dropped_off',
        location: currentLocation
      });
      
      toast.success('POD signed & load marked as delivered!');
      setShowSignature(false);
      api.get(`/driver/load/${loadNumber}`).then(res => setLoadData(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmittingSignature(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await api.post(`/driver/load/${loadNumber}/messages`, { content: newMessage.trim() });
      setNewMessage('');
      loadMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const searchLoad = (e) => {
    e.preventDefault();
    setLoading(true);
    api.get(`/driver/load/${loadNumber}`)
      .then(res => {
        setLoadData(res.data);
        setIsTracking(res.data.load.is_tracking_active || false);
        toast.success('Load found!');
      })
      .catch(() => toast.error('Load not found. Please check the reference number.'))
      .finally(() => setLoading(false));
  };

  const updateStatus = (newStatus) => {
    if (!window.confirm(`Update load status to "${newStatus}"?`)) return;

    const statusData = { status: newStatus };
    
    if (currentLocation) {
      statusData.location = currentLocation;
    }

    api.post(`/driver/load/${loadNumber}/update-status`, statusData)
      .then(() => {
        toast.success('Status updated successfully!');
        api.get(`/driver/load/${loadNumber}`).then(res => setLoadData(res.data));
      })
      .catch(() => toast.error('Failed to update status'));
  };

  const toggleTracking = () => {
    const newTrackingState = !isTracking;
    
    api.post(`/driver/load/${loadNumber}/toggle-tracking`, { is_active: newTrackingState })
      .then(() => {
        setIsTracking(newTrackingState);
        if (newTrackingState) {
          startLocationTracking();
          toast.success('Time tracking started');
        } else {
          stopLocationTracking();
          toast.success('Time tracking paused');
        }
      })
      .catch(() => toast.error('Failed to toggle tracking'));
  };

  const startLocationTracking = () => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(location);
          
          api.post(`/driver/load/${loadNumber}/location`, location)
            .catch(err => console.error('Failed to update location:', err));
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Unable to get location. Please enable GPS.');
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          timeout: 27000
        }
      );
      window.driverWatchId = watchId;
    }
  };

  const stopLocationTracking = () => {
    if (window.driverWatchId) {
      navigator.geolocation.clearWatch(window.driverWatchId);
    }
  };

  // Incident reporting functions
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/incidents/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIncidentImages(prev => [...prev, res.data.filename]);
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const submitIncident = async () => {
    if (!incidentType) {
      toast.error('Please select an incident type');
      return;
    }
    if (!incidentDescription.trim()) {
      toast.error('Please describe the incident');
      return;
    }

    setSubmittingIncident(true);
    try {
      await api.post(`/driver/load/${loadNumber}/incident`, {
        incident_type: incidentType,
        severity: incidentSeverity,
        description: incidentDescription,
        images: incidentImages,
        location: currentLocation ? `${currentLocation.lat}, ${currentLocation.lng}` : null,
        location_coords: currentLocation
      });
      
      toast.success('Incident reported successfully!');
      setShowIncidentDialog(false);
      resetIncidentForm();
    } catch (err) {
      toast.error('Failed to report incident');
    } finally {
      setSubmittingIncident(false);
    }
  };

  const resetIncidentForm = () => {
    setIncidentType('');
    setIncidentSeverity('medium');
    setIncidentDescription('');
    setIncidentImages([]);
  };

  if (!loadData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          {/* PWA Install Banner */}
          {showInstallBanner && (
            <div className="bg-blue-600 text-white rounded-xl p-4 flex items-center gap-3 shadow-lg" data-testid="install-banner">
              <Smartphone className="w-8 h-8 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-sm">Install Driver App</p>
                <p className="text-xs text-blue-100">Add to your home screen for quick access</p>
              </div>
              {installPrompt ? (
                <Button size="sm" variant="secondary" onClick={handleInstall} data-testid="install-btn">
                  <Download className="w-4 h-4 mr-1" /> Install
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setShowInstallBanner(false)}>
                  Dismiss
                </Button>
              )}
            </div>
          )}

          {/* iOS Install Instructions (no beforeinstallprompt on Safari) */}
          {!showInstallBanner && !window.matchMedia('(display-mode: standalone)').matches && /iPhone|iPad/.test(navigator.userAgent) && (
            <div className="bg-slate-800 text-white rounded-xl p-4 text-center" data-testid="ios-install-hint">
              <Smartphone className="w-6 h-6 mx-auto mb-2" />
              <p className="text-sm font-medium">Install this app on your iPhone</p>
              <p className="text-xs text-slate-300 mt-1">Tap the share button, then "Add to Home Screen"</p>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Driver Portal</CardTitle>
                  <p className="text-sm text-slate-600">Enter your load reference number</p>
                </div>
              </div>
            </CardHeader>
          <CardContent>
            <form onSubmit={searchLoad} className="space-y-4">
              <div>
                <Label>Load Reference Number *</Label>
                <Input
                  required
                  placeholder="LOAD-20251119012345"
                  value={loadNumber}
                  onChange={(e) => setLoadNumber(e.target.value)}
                  className="text-lg"
                  data-testid="load-number-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="access-load-btn">
                {loading ? 'Searching...' : 'Access Load'}
              </Button>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  const { load, driver, carrier, shipper } = loadData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl">Load {load.load_number}</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Driver: {driver.name}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                load.status === 'completed' ? 'bg-green-100 text-green-700' :
                load.status === 'dropped_off' ? 'bg-green-100 text-green-700' :
                load.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {load.status.replace(/_/g, ' ').toUpperCase()}
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Pickup & Delivery Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-l-4 border-blue-600">
            <CardHeader className="pb-2">
              <p className="text-sm font-semibold text-blue-600">📦 PICKUP</p>
            </CardHeader>
            <CardContent>
              <p className="font-bold">{shipper?.legal_name || 'Shipper'}</p>
              <p className="text-sm text-slate-600">{load.pickup_location}</p>
              <p className="text-sm text-slate-600">{load.pickup_city}, {load.pickup_state}</p>
              <div className="flex items-center mt-2 text-sm">
                <Clock className="w-4 h-4 mr-1 text-slate-400" />
                {load.pickup_date} at {load.pickup_time}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-600">
            <CardHeader className="pb-2">
              <p className="text-sm font-semibold text-green-600">📍 DELIVERY</p>
            </CardHeader>
            <CardContent>
              <p className="font-bold">{load.delivery_location}</p>
              <p className="text-sm text-slate-600">{load.delivery_city}, {load.delivery_state}</p>
              <div className="flex items-center mt-2 text-sm">
                <Clock className="w-4 h-4 mr-1 text-slate-400" />
                {load.delivery_date} at {load.delivery_time}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Load Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Load Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Commodity</p>
              <p className="font-medium">{load.commodity}</p>
            </div>
            <div>
              <p className="text-slate-500">Weight</p>
              <p className="font-medium">{load.weight} lbs</p>
            </div>
            <div>
              <p className="text-slate-500">Equipment</p>
              <p className="font-medium">{load.equipment_type}</p>
            </div>
            {load.temperature && (
              <div>
                <p className="text-slate-500">Temperature</p>
                <p className="font-medium">{load.temperature}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Special Instructions */}
        {load.special_instructions && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="py-4">
              <p className="text-sm font-semibold text-yellow-700 mb-1">⚠️ Special Instructions</p>
              <p className="text-sm text-yellow-800">{load.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Time Tracking */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Time Tracking</CardTitle>
              <Button 
                variant={isTracking ? "destructive" : "default"}
                size="sm"
                onClick={toggleTracking}
                data-testid="toggle-tracking-btn"
              >
                {isTracking ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {isTracking ? 'Pause' : 'Start'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
              <span className="text-sm">
                {isTracking ? 'Tracking active' : 'Tracking paused'}
              </span>
              {load.total_drive_time_minutes > 0 && (
                <span className="text-sm text-slate-600">
                  Total time: {Math.floor(load.total_drive_time_minutes / 60)}h {Math.round(load.total_drive_time_minutes % 60)}m
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Update Buttons */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Update Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {load.status === 'booked' && (
              <Button className="w-full bg-blue-600" onClick={() => updateStatus('en_route_to_pickup')} data-testid="status-en-route-btn">
                <Truck className="w-4 h-4 mr-2" />
                En Route to Pickup
              </Button>
            )}
            
            {load.status === 'en_route_to_pickup' && (
              <Button className="w-full bg-yellow-600" onClick={() => updateStatus('at_pickup')} data-testid="status-at-pickup-btn">
                <MapPin className="w-4 h-4 mr-2" />
                Arrived at Pickup
              </Button>
            )}
            
            {load.status === 'at_pickup' && (
              <Button className="w-full bg-green-600" onClick={() => updateStatus('picked_up')} data-testid="status-picked-up-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Load Picked Up
              </Button>
            )}
            
            {load.status === 'picked_up' && (
              <Button className="w-full bg-blue-600" onClick={() => updateStatus('in_transit')} data-testid="status-in-transit-btn">
                <Truck className="w-4 h-4 mr-2" />
                In Transit
              </Button>
            )}
            
            {load.status === 'in_transit' && (
              <Button className="w-full bg-orange-600" onClick={() => updateStatus('at_delivery')} data-testid="status-at-delivery-btn">
                <MapPin className="w-4 h-4 mr-2" />
                Arrived at Delivery
              </Button>
            )}
            
            {load.status === 'at_delivery' && !showSignature && (
              <Button className="w-full bg-green-600" onClick={() => { setShowSignature(true); initCanvas(); }} data-testid="status-dropped-off-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Load Dropped Off — Sign POD
              </Button>
            )}

            {/* Signature Capture */}
            {load.status === 'at_delivery' && showSignature && (
              <div className="space-y-3 border-2 border-green-300 rounded-lg p-4 bg-green-50" data-testid="signature-section">
                <p className="text-sm font-bold text-green-800">POD Signature Required</p>
                <p className="text-xs text-green-700">Upload your POD photo first (above), then sign here. The signature will be stamped directly on the POD document.</p>
                {driverDocs.filter(d => d.document_type === 'pod').length === 0 && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 text-xs text-amber-800">
                    No POD uploaded yet — upload a POD photo above before signing so the signature gets stamped on it.
                  </div>
                )}
                <div>
                  <Label className="text-xs">Receiver Name *</Label>
                  <Input
                    placeholder="Name of person receiving load"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    data-testid="signer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Delivery Date *</Label>
                  <Input
                    type="date"
                    value={deliveryDateConfirm}
                    onChange={(e) => setDeliveryDateConfirm(e.target.value)}
                    data-testid="delivery-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Signature *</Label>
                  <canvas
                    ref={canvasRef}
                    className="w-full bg-white border-2 border-slate-300 rounded-lg cursor-crosshair touch-none"
                    style={{ height: '200px' }}
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={endDraw}
                    data-testid="signature-canvas"
                  />
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-xs text-blue-600 hover:underline mt-1"
                    data-testid="clear-signature-btn"
                  >
                    Clear Signature
                  </button>
                </div>
                <Button
                  className="w-full bg-green-600"
                  onClick={submitSignatureAndDropoff}
                  disabled={submittingSignature}
                  data-testid="submit-pod-btn"
                >
                  {submittingSignature ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Confirm Delivery & Sign POD</>
                  )}
                </Button>
              </div>
            )}
            
            {load.status === 'dropped_off' && (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-2" />
                <p className="text-lg font-bold text-green-700">Load Completed!</p>
                <p className="text-sm text-slate-600">Thank you for your service</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Document Upload - BOL, POD, etc. */}
        <Card data-testid="driver-doc-upload">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-green-600" />
              Upload Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-3">Take a photo or upload BOL, POD, and other documents.</p>
            
            {/* Doc Type Selector */}
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { value: 'bol', label: 'BOL' },
                { value: 'pod', label: 'POD' },
                { value: 'rate_con', label: 'Rate Con' },
                { value: 'lumper', label: 'Lumper Receipt' },
                { value: 'scale_ticket', label: 'Scale Ticket' },
                { value: 'other', label: 'Other' },
              ].map(dt => (
                <button
                  key={dt.value}
                  type="button"
                  onClick={() => setDocType(dt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    docType === dt.value
                      ? 'bg-green-50 border-green-400 text-green-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`doc-type-${dt.value}`}
                >
                  {dt.label}
                </button>
              ))}
            </div>

            {/* Upload Button - uses camera on mobile */}
            <label className={`flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              uploadingDoc ? 'border-slate-300 bg-slate-50' : 'border-green-300 bg-green-50 hover:bg-green-100'
            }`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                capture="environment"
                onChange={handleDocUpload}
                disabled={uploadingDoc}
                className="hidden"
                data-testid="doc-upload-input"
              />
              {uploadingDoc ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
              ) : (
                <Camera className="w-5 h-5 text-green-600" />
              )}
              <span className={`text-sm font-medium ${uploadingDoc ? 'text-slate-500' : 'text-green-700'}`}>
                {uploadingDoc ? 'Uploading...' : `Take Photo / Upload ${docType.toUpperCase()}`}
              </span>
            </label>

            {/* Uploaded docs list */}
            {driverDocs.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase">Uploaded Documents</p>
                {driverDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg" data-testid={`uploaded-doc-${doc.id}`}>
                    <div className={`px-2 py-1 rounded text-xs font-bold ${
                      doc.document_type === 'bol' ? 'bg-blue-100 text-blue-700' :
                      doc.document_type === 'pod' ? 'bg-green-100 text-green-700' :
                      doc.document_type === 'rate_con' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {doc.document_type?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                      <p className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleString()}</p>
                    </div>
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages */}
        <Card data-testid="driver-messages-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg bg-slate-50 h-64 overflow-y-auto p-3 mb-3 space-y-2">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No messages yet. Send one to your dispatcher.</p>
              ) : (
                messages.map(msg => {
                  const isDriver = msg.sender_type === 'driver';
                  return (
                    <div key={msg.id} className={`flex ${isDriver ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                        isDriver ? 'bg-blue-600 text-white' : 'bg-white border text-slate-900'
                      }`}>
                        {!isDriver && <p className="text-xs font-medium text-blue-600 mb-0.5">{msg.sender_name}</p>}
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isDriver ? 'text-blue-200' : 'text-slate-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                data-testid="driver-message-input"
              />
              <Button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()} size="icon" data-testid="driver-send-message-btn">
                {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Incident Reporting */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Report an Incident
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600 mb-3">
              Report any issues: trailer damage, accidents, cargo problems, or other incidents.
            </p>
            <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
              <DialogTrigger asChild>
                <Button variant="destructive" className="w-full" data-testid="report-incident-btn">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Report Incident
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Report an Incident</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Incident Type *</Label>
                    <Select value={incidentType} onValueChange={setIncidentType}>
                      <SelectTrigger data-testid="incident-type-select">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trailer_damage">Trailer Damage</SelectItem>
                        <SelectItem value="accident">Accident</SelectItem>
                        <SelectItem value="cargo_issue">Cargo Issue</SelectItem>
                        <SelectItem value="breakdown">Vehicle Breakdown</SelectItem>
                        <SelectItem value="weather_delay">Weather Delay</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select value={incidentSeverity} onValueChange={setIncidentSeverity}>
                      <SelectTrigger data-testid="incident-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - Minor issue</SelectItem>
                        <SelectItem value="medium">Medium - Needs attention</SelectItem>
                        <SelectItem value="high">High - Urgent</SelectItem>
                        <SelectItem value="critical">Critical - Emergency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={incidentDescription}
                      onChange={(e) => setIncidentDescription(e.target.value)}
                      placeholder="Describe what happened..."
                      className="min-h-[100px]"
                      data-testid="incident-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Photos (Optional)</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {incidentImages.map((img, i) => (
                        <div key={i} className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center">
                          <Camera className="w-6 h-6 text-slate-500" />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="flex-1"
                        data-testid="incident-image-input"
                      />
                      {uploadingImage && <Loader2 className="w-5 h-5 animate-spin" />}
                    </div>
                    <p className="text-xs text-slate-500">{incidentImages.length} image(s) attached</p>
                  </div>

                  {currentLocation && (
                    <div className="bg-slate-100 p-3 rounded text-sm">
                      <p className="font-medium">📍 Current Location</p>
                      <p className="text-slate-600">
                        Lat: {currentLocation.lat.toFixed(4)}, Lng: {currentLocation.lng.toFixed(4)}
                      </p>
                    </div>
                  )}

                  <Button 
                    onClick={submitIncident} 
                    className="w-full" 
                    disabled={submittingIncident}
                    data-testid="submit-incident-btn"
                  >
                    {submittingIncident ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Incident Report'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Current Location */}
        {currentLocation && (
          <Card>
            <CardContent className="py-4">
              <p className="text-xs text-slate-600">Current Location</p>
              <p className="text-sm font-mono">
                Lat: {currentLocation.lat.toFixed(6)}, Lng: {currentLocation.lng.toFixed(6)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <Button variant="outline" className="w-full" onClick={() => setLoadData(null)} data-testid="back-btn">
          ← Access Different Load
        </Button>
      </div>
    </div>
  );
}
